import React, { useState, useEffect } from 'react';
import { db, functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { toast } from 'react-toastify';
import {
    Box,
    Typography,
    Paper,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    CircularProgress,
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Tooltip,
    IconButton,
    DialogContentText,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PersonAddIcon from '@mui/icons-material/PersonAdd'; // NEW ICON
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import FirmDetailView from './FirmDetailView'; // NEW IMPORT

const FirmManagementPage = () => {
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [newOrgName, setNewOrgName] = useState('');
    const [newOwnerEmail, setNewOwnerEmail] = useState('');
    const [newPortalTier, setNewPortalTier] = useState('read-only');
    // New State for delete confirmation
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [firmToDelete, setFirmToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [selectedFirm, setSelectedFirm] = useState(null); // NEW STATE
    
    // State for Promote Admin modal
    const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
    const [firmToPromote, setFirmToPromote] = useState(null);
    const [promoteEmail, setPromoteEmail] = useState('');
    const [promoteRole, setPromoteRole] = useState('admin');
    const [isPromoting, setIsPromoting] = useState(false);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setLoading(true);
        setError(null);
        const orgsQuery = query(collection(db, 'organizations'), orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(orgsQuery);
        const orgsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setOrganizations(orgsData);
      } catch (err) {
        console.error("Error fetching organizations:", err);
        setError('Failed to fetch organizations. Please check the console for more details.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, [refreshTrigger]);

  const handleOpenModal = () => {
    setNewOrgName('');
    setNewOwnerEmail('');
    setNewPortalTier('read-only');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleCreateOrganization = async () => {
    if (!newOrgName.trim() || !newOwnerEmail.trim()) {
      toast.error("Firm Name and Owner Email are required.");
      return;
    }

    setIsCreating(true);
    try {
      const adminCreateOrganization = httpsCallable(functions, 'adminCreateOrganization');
      
      const payload = {
        organizationName: newOrgName,
        ownerEmail: newOwnerEmail,
        firmPortalTier: newPortalTier,
      };
      console.log('Payload being sent to function:', payload); // Diagnostic log added

      const result = await adminCreateOrganization(payload);

      if (result.data.success) {
        toast.success(`Successfully created firm "${newOrgName}".`);
        handleCloseModal();
        setRefreshTrigger(prev => prev + 1); // Trigger a refresh
      } else {
        throw new Error(result.data.error || 'An unknown error occurred.');
      }
    } catch (err) {
      console.error("Error creating organization:", err);
      toast.error(`Failed to create firm: ${err.message}`);
    } finally {
      setIsCreating(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp || !timestamp.toDate) {
      return 'N/A';
    }
    return timestamp.toDate().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
};

const handleEditFirm = (firm) => {
    console.log('Edit firm clicked:', firm.name); // Debug log
    setSelectedFirm(firm);
};

const handleBackToFirmList = () => {
    setSelectedFirm(null);
    setRefreshTrigger(prev => prev + 1);
};

const handleResetPassword = async (ownerId, ownerEmail) => {
    if (!ownerEmail) {
        toast.error("Owner's email is not available.");
        return;
    }
    const auth = getAuth();
    try {
        await sendPasswordResetEmail(auth, ownerEmail);
        toast.success(`Password reset email sent to ${ownerEmail}`);
    } catch (error) {
        console.error("Error sending password reset:", error);
        toast.error(`Failed to send reset email: ${error.message}`);
    }
};

const handleDeleteFirm = (firm) => {
    setFirmToDelete(firm);
    setDeleteDialogOpen(true);
};

const confirmDeleteFirm = async () => {
    if (!firmToDelete) return;
    setIsDeleting(true);
    try {
        const adminDeleteFirm = httpsCallable(functions, 'adminDeleteFirm');
        const result = await adminDeleteFirm({ firmId: firmToDelete.id, ownerId: firmToDelete.ownerId });
        if (result.data.success) {
            toast.success(`Firm "${firmToDelete.name}" and its owner have been deleted.`);
            setRefreshTrigger(prev => prev + 1); // Refresh the list
        } else {
            throw new Error(result.data.error || 'An unknown error occurred.');
        }
    } catch (err) {
        console.error("Error deleting firm:", err);
        toast.error(`Failed to delete firm: ${err.message}`);
    } finally {
        setIsDeleting(false);
        setDeleteDialogOpen(false);
        setFirmToDelete(null);
    }
};

// NEW: Handle Promote Admin dialog
const handleOpenPromoteDialog = (firm) => {
    setFirmToPromote(firm);
    setPromoteEmail('');
    setPromoteRole('admin');
    setPromoteDialogOpen(true);
};

const handleClosePromoteDialog = () => {
    setPromoteDialogOpen(false);
    setFirmToPromote(null);
    setPromoteEmail('');
    setPromoteRole('admin');
};
const handlePromoteAdmin = async () => {
    if (!promoteEmail.trim()) {
        toast.error("User email is required.");
        return;
    }

    if (!firmToPromote) {
        toast.error("No firm selected.");
        return;
    }

    setIsPromoting(true);
    try {
        const adminPromoteOrgAdmin = httpsCallable(functions, 'adminPromoteOrgAdmin');
        const result = await adminPromoteOrgAdmin({
            userEmail: promoteEmail.trim(),
            organizationId: firmToPromote.id,
            firmPortalRole: promoteRole
        });

        if (result.data.success) {
            if (result.data.alreadyAdmin) {
                toast.info(result.data.message);
            } else {
                toast.success(result.data.message);
            }
            handleClosePromoteDialog();
            setRefreshTrigger(prev => prev + 1); // Refresh the list
        } else {
            throw new Error(result.data.error || 'An unknown error occurred.');
        }
    } catch (err) {
        console.error("Error promoting user to orgAdmin:", err);
        toast.error(`Failed to promote user: ${err.message}`);
    } finally {
        setIsPromoting(false);
    }
};

if (selectedFirm) {
    return (
        <FirmDetailView
            firm={selectedFirm}
            onBack={handleBackToFirmList}
        />
    );
}

return (
    <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Firms / Organizations
        </Typography>
        <Button variant="contained" onClick={handleOpenModal}>
          + Create New Firm
        </Button>
      </Box>

      {loading && <CircularProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      {!loading && !error && (
        <Paper elevation={0} variant="outlined">
          <TableContainer>
            <Table>
              <TableHead>
    <TableRow>
        <TableCell sx={{ fontWeight: 'bold' }}>Firm Name</TableCell>
        <TableCell sx={{ fontWeight: 'bold' }}>Portal Tier</TableCell>
        <TableCell sx={{ fontWeight: 'bold' }}>Owner (orgAdmin)</TableCell>
        <TableCell sx={{ fontWeight: 'bold' }}>Date Created</TableCell>
        <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
    </TableRow>
</TableHead>
<TableBody>
    {organizations.length > 0 ? (
        organizations.map((org) => (
            <TableRow key={org.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell>{org.name}</TableCell>
                <TableCell>
                    <Chip 
                        label={org.firmPortalTier === 'enterprise' ? 'Enterprise' : 'Read-Only'}
                        color={org.firmPortalTier === 'enterprise' ? 'primary' : 'default'}
                        size="small"
                    />
                </TableCell>
                <TableCell>{org.ownerEmail}</TableCell>
                <TableCell>{formatDate(org.createdAt)}</TableCell>
                <TableCell align="right">
                    <Tooltip title="Add User to Firm Portal">
                        <IconButton size="small" color="primary" onClick={() => handleOpenPromoteDialog(org)}>
                            <PersonAddIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Firm Details">
                        <IconButton size="small" onClick={() => handleEditFirm(org)}>
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Reset Owner's Password">
                        <IconButton size="small" onClick={() => handleResetPassword(org.ownerId, org.ownerEmail)}>
                            <VpnKeyIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Firm">
                        <IconButton size="small" color="error" onClick={() => handleDeleteFirm(org)}>
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </TableCell>
            </TableRow>
        ))
    ) : (
        <TableRow>
            <TableCell colSpan={5} align="center">
                No organizations found.
            </TableCell>
        </TableRow>
    )}
</TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Create Firm Modal */}
      <Dialog open={isModalOpen} onClose={handleCloseModal}>
        <DialogTitle>Create New Firm</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Firm Name"
            type="text"
            fullWidth
            variant="standard"
            value={newOrgName}
            onChange={(e) => setNewOrgName(e.target.value)}
          />
          <TextField
            margin="dense"
            id="email"
            label="Owner Email (orgAdmin)"
            type="email"
            fullWidth
            variant="standard"
            value={newOwnerEmail}
            onChange={(e) => setNewOwnerEmail(e.target.value)}
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="portal-tier-label">Portal Tier</InputLabel>
            <Select
              labelId="portal-tier-label"
              id="portal-tier"
              value={newPortalTier}
              label="Portal Tier"
              onChange={(e) => setNewPortalTier(e.target.value)}
            >
              <MenuItem value="read-only">Read-Only (Activity Feed + Alerts)</MenuItem>
              <MenuItem value="enterprise">Enterprise (Full Dashboard + Management)</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={isCreating}>Cancel</Button>
          <Button onClick={handleCreateOrganization} variant="contained" disabled={isCreating}>
            {isCreating ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Promote Admin Modal */}
      <Dialog open={promoteDialogOpen} onClose={handleClosePromoteDialog}>
        <DialogTitle>Add Firm Portal User</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Grant access to a user for <strong>{firmToPromote?.name}</strong>.
          </DialogContentText>
          <Alert severity="info" sx={{ mb: 2 }}>
            Enter any email address. If the user doesn't exist yet, they'll receive an invitation to set up their password and access the Firm Portal.
          </Alert>
          <TextField
            autoFocus
            margin="dense"
            id="promote-email"
            label="User Email"
            type="email"
            fullWidth
            variant="standard"
            value={promoteEmail}
            onChange={(e) => setPromoteEmail(e.target.value)}
            helperText="Enter the email of the user to add"
          />
          
          {/* Only show role selector for Enterprise tier firms */}
          {firmToPromote?.firmPortalTier === 'enterprise' && (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="promote-role-label">Firm Portal Role</InputLabel>
              <Select
                labelId="promote-role-label"
                id="promote-role"
                value={promoteRole}
                label="Firm Portal Role"
                onChange={(e) => setPromoteRole(e.target.value)}
              >
                <MenuItem value="admin">Admin (Full access + User Management)</MenuItem>
                <MenuItem value="user">User (Full access, no User Management)</MenuItem>
              </Select>
            </FormControl>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClosePromoteDialog} disabled={isPromoting}>Cancel</Button>
          <Button onClick={handlePromoteAdmin} variant="contained" disabled={isPromoting}>
            {isPromoting ? <CircularProgress size={24} /> : 'Grant Access'}
          </Button>
        </DialogActions>
      </Dialog>

    {/* Delete Confirmation Dialog */}
    <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Firm Deletion</DialogTitle>
        <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
                This is a permanent action and cannot be undone.
            </Alert>
            <DialogContentText>
                Are you sure you want to delete the firm "<strong>{firmToDelete?.name}</strong>"?
            </DialogContentText>
            <DialogContentText sx={{ mt: 1 }}>
                This will also permanently delete the owner's account (<strong>{firmToDelete?.ownerEmail}</strong>).
            </DialogContentText>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
                Cancel
            </Button>
            <Button
                onClick={confirmDeleteFirm}
                variant="contained"
                color="error"
                disabled={isDeleting}
            >
                {isDeleting ? <CircularProgress size={24} /> : 'Delete Firm'}
            </Button>
        </DialogActions>
    </Dialog>
</Box>
);
};

export default FirmManagementPage;